import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// studio.table.header.menuItem.*
// studio.table.header.menu.*
import { PapiDeviation } from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex';
import {
  BiData,
  StudioBatchUpdateBiEvent,
  UnlistItemConfirmBiEvent,
  SellYourArtClickBiEvent,
  BiContextProvidedValue,
} from '@wix/da-bi/pkg/events';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext/ScreenSizeContext';
import TextButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/TextButtonWithContextMenu';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import ArrowDown from '@wix/da-ds/pkg/Icons/24x24/ArrowDown';
import CoreIcon from '@wix/da-ds/pkg/Icons/24x24/Core';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useUnpublishFlow } from '@wix/da-shared-react/pkg/utils/hooks/useUnpublishFlow';
import { OnUnpublishCompleteAction } from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import { BatchAction, StudioSection } from '../../../../types/store';
import MoveToFolderMenu from '../../../stash/MoveToFolderMenu';
import { useBatchSellAction } from '../BatchSellGhostButton/useBatchSellAction';
import BatchSellSubmenu from './BatchSellSubmenu';

import s from './MenuSection.scss';

export interface Props {
  className?: string;
  activeSection: StudioSection;
  selectedCount?: number;
  contentType?: string;
  tiers?: PapiDeviation[];
  isLoading?: boolean;
  isRootFolder?: boolean;
  isUnpublishedDataset?: boolean;
  batchMerge: () => void;
  batchLoadTiers: () => void;
  selectBatchAction: (batchAction: BatchAction) => any;
  pushTierCreateModal: () => void;
  confirmMoveSelectionToFolder: (targetFolderId: number) => void;
  batchMoveToStash: () => void;
  batchMoveToDrafts: () => void;
  batchUnpublish: () => void;
  createFolderFromSelection: () => void;
  withCoreSymbol?: boolean;
  onScheduleClick?: () => void;
}

const MENU_GROUPS = {
  edit: {
    label: 'edit',
    items: [
      {
        excludeSection: [StudioSection.JOURNALS],
        excludeContentType: { [StudioSection.PUBLISHED]: 'posts' },
        items: [
          {
            label: 'title',
            value: BatchAction.EDIT_TITLE,
          },
          {
            label: 'tags',
            value: BatchAction.EDIT_TAGS,
            excludeSection: [StudioSection.APPS, StudioSection.APPS_UNLISTED],
          },
          {
            label: 'description',
            value: BatchAction.EDIT_DESCRIPTION,
          },
        ],
      },
      {
        items: [
          {
            label: 'gallery',
            value: BatchAction.EDIT_GALLERY,
            excludeSection: [StudioSection.JOURNALS],
            excludeContentType: { [StudioSection.PUBLISHED]: 'posts' },
          },
          {
            label: 'tier',
            value: BatchAction.EDIT_TIER,
            withCoreSymbol: true,
            excludeSection: [StudioSection.APPS, StudioSection.APPS_UNLISTED],
          },
          {
            label: 'display_options',
            value: BatchAction.EDIT_DISPLAY_OPTIONS,
            excludeSection: [
              StudioSection.JOURNALS,
              StudioSection.APPS,
              StudioSection.APPS_UNLISTED,
            ],
            excludeContentType: { [StudioSection.PUBLISHED]: 'posts' },
          },
          {
            label: 'license',
            value: BatchAction.EDIT_LICENSE,
            excludeSection: [
              StudioSection.JOURNALS,
              StudioSection.APPS,
              StudioSection.APPS_UNLISTED,
            ],
            excludeContentType: { [StudioSection.PUBLISHED]: 'posts' },
          },
          {
            label: 'comments',
            value: BatchAction.EDIT_COMMENTS,
            excludeSection: [StudioSection.JOURNALS, StudioSection.APPS],
            excludeContentType: { [StudioSection.PUBLISHED]: 'posts' },
          },
          {
            label: 'labels',
            value: BatchAction.EDIT_LABELS,
            excludeSection: [
              StudioSection.JOURNALS,
              StudioSection.APPS,
              StudioSection.APPS_UNLISTED,
            ],
            excludeContentType: { [StudioSection.PUBLISHED]: 'posts' },
          },
          {
            label: 'preset',
            value: BatchAction.EDIT_PRESET,
            section: [
              StudioSection.DRAFTS,
              StudioSection.LITERATURE,
              StudioSection.SCHEDULED,
              StudioSection.STASH,
              StudioSection.PLAYGROUND,
            ],
          },
        ],
      },
    ],
  },
  booleans: {
    label: 'booleans',
    excludeSection: [
      StudioSection.JOURNALS,
      StudioSection.APPS,
      StudioSection.APPS_UNLISTED,
    ],
    excludeContentType: { [StudioSection.PUBLISHED]: 'posts' },
    items: [
      {
        items: [
          {
            label: 'ai_add',
            value: BatchAction.BOOL_AI_ADD,
          },
          {
            label: 'ai_del',
            value: BatchAction.BOOL_AI_DEL,
          },
        ],
      },
      {
        items: [
          {
            label: 'noai_add',
            value: BatchAction.BOOL_NOAI_ADD,
          },
          {
            label: 'noai_del',
            value: BatchAction.BOOL_NOAI_DEL,
          },
        ],
      },
      {
        items: [
          {
            label: 'mature_add',
            value: BatchAction.BOOL_MATURE_ADD,
          },
          {
            label: 'mature_del',
            value: BatchAction.BOOL_MATURE_DEL,
          },
        ],
      },
    ],
  },
  other: {
    label: 'other',
    items: [
      {
        items: [
          {
            label: 'sell',
            value: BatchAction.OTHER_SELL,
            section: [
              StudioSection.DRAFTS,
              StudioSection.PLAYGROUND,
              StudioSection.STASH,
            ],
            excludeContentType: {
              [StudioSection.DRAFTS]: 'posts',
              [StudioSection.PLAYGROUND]: 'posts',
              [StudioSection.STASH]: 'posts',
            },
          },
          {
            label: 'folders.moveToFolder',
            section: [StudioSection.STASH],
            value: BatchAction.OTHER_MOVE_TO_FOLDER,
          },
          {
            label: ({ isRootFolder }) =>
              isRootFolder
                ? 'folders.createFromSelection.root'
                : 'folders.createFromSelection',
            section: [StudioSection.STASH],
            value: BatchAction.OTHER_CREATE_FOLDER_FROM_SELECTION,
          },
          {
            label: 'merge',
            value: BatchAction.OTHER_MERGE,
            section: [
              StudioSection.DRAFTS,
              StudioSection.LITERATURE,
              StudioSection.SCHEDULED,
              StudioSection.STASH,
              StudioSection.PLAYGROUND,
            ],
          },
          {
            label: 'moveToStash',
            value: BatchAction.OTHER_MOVE_TO_STASH,
            section: [
              StudioSection.DRAFTS,
              StudioSection.LITERATURE,
              StudioSection.JOURNALS,
              StudioSection.PLAYGROUND,
            ],
            biUpdateType: 'move_to_stash',
          },
          {
            label: 'moveToDrafts',
            value: BatchAction.OTHER_MOVE_TO_DRAFTS,
            section: [StudioSection.PLAYGROUND],
            biUpdateType: 'move_to_drafts',
          },
          {
            label: 'unpublish',
            value: BatchAction.OTHER_UNPUBLISH,
            section: [
              StudioSection.PUBLISHED,
              StudioSection.PRODUCTS_EXCLUSIVE,
              StudioSection.PRODUCTS_PCP,
            ],
            biUpdateType: 'unlist',
          },
          {
            label: 'schedule',
            value: BatchAction.EDIT_SCHEDULE,
          },
        ],
      },
      {
        items: [
          {
            label: 'delete',
            value: BatchAction.OTHER_DELETE,
          },
        ],
      },
    ],
  },
};

const MenuSection: FC<Props> = props => {
  const {
    className,
    activeSection,
    selectedCount,
    contentType,
    tiers,
    isLoading,
    isUnpublishedDataset,
    selectBatchAction,
    batchMerge,
    batchLoadTiers,
    batchMoveToStash,
    batchMoveToDrafts,
    batchUnpublish,
    pushTierCreateModal,
    confirmMoveSelectionToFolder,
    createFolderFromSelection,
    onScheduleClick,
  } = props;
  const { t } = useTranslation();
  const flags = useEnabledFlagList();
  const screenSize = useContext(ScreenSizeContext);
  const isSmallDesktop = screenSize.width <= 1100;
  const {
    selectedDeviations,
    selectedIds,
    handleClick: handleBatchSellClick,
    submenuItems: batchSellSubmenuItems,
  } = useBatchSellAction();

  const initiateUnpublishFlow = useUnpublishFlow({
    onConfirmOverride: batchUnpublish,
    onCompleteAction: OnUnpublishCompleteAction.Toast,
    isPlural: selectedCount !== 1,
    confirmBtnBiData: BiData<UnlistItemConfirmBiEvent>({
      evid: 359,
      sectionname: 'batch_actions',
    }),
  });

  const onSelect = useCallback(
    action => {
      switch (action) {
        // If the "edit tier" action was selected but there's
        // no tiers, open create tier modal
        case BatchAction.EDIT_TIER:
          if (Array.isArray(tiers) && tiers.length === 0) {
            pushTierCreateModal();
          } else {
            selectBatchAction(action);
          }
          break;

        case BatchAction.OTHER_CREATE_FOLDER_FROM_SELECTION:
          createFolderFromSelection();
          break;

        case BatchAction.OTHER_MERGE:
          batchMerge();
          break;

        case BatchAction.OTHER_MOVE_TO_STASH:
          batchMoveToStash();
          break;

        case BatchAction.OTHER_MOVE_TO_DRAFTS:
          batchMoveToDrafts();
          break;

        case BatchAction.OTHER_UNPUBLISH:
          initiateUnpublishFlow();
          break;

        case BatchAction.OTHER_SELL:
          handleBatchSellClick();
          break;

        case BatchAction.EDIT_SCHEDULE:
          if (onScheduleClick) {
            onScheduleClick();
          }
          break;

        default:
          selectBatchAction(action);
          break;
      }
    },
    [
      tiers,
      batchMerge,
      selectBatchAction,
      pushTierCreateModal,
      createFolderFromSelection,
      batchMoveToStash,
      batchMoveToDrafts,
      initiateUnpublishFlow,
      handleBatchSellClick,
      onScheduleClick,
    ]
  );

  const filterBySection = useCallback(
    item =>
      (!item.section || item.section.includes(activeSection)) &&
      (!item.excludeSection || !item.excludeSection.includes(activeSection)) &&
      (!item.excludeContentType ||
        !(
          Object.keys(item.excludeContentType).includes(activeSection) &&
          item.excludeContentType[activeSection] === contentType
        )),
    [activeSection, contentType]
  );

  const menuItems = useMemo(
    () =>
      Object.keys(MENU_GROUPS)
        .filter(groupKey => filterBySection(MENU_GROUPS[groupKey]))
        .reduce((groups, groupKey) => {
          const group = MENU_GROUPS[groupKey];
          groups[groupKey] = group.items
            .filter(filterBySection)
            .map(subGroup => ({
              items: subGroup.items
                .filter(filterBySection)
                // include item if feature is enabled
                .filter(item => !item?.feature || flags.includes(item.feature))
                // exclude item if feature_exclude is enabled
                .filter(
                  item =>
                    !item?.feature_exclude ||
                    !flags.includes(item.feature_exclude)
                )
                .map(item => ({
                  ...item,
                  label:
                    typeof item.label === 'function'
                      ? item.label(props)
                      : item.label,
                }))
                .filter(item => {
                  // Filter out schedule if not at small desktop or not unpublished dataset
                  if (
                    item.value === BatchAction.EDIT_SCHEDULE &&
                    (!isSmallDesktop || !isUnpublishedDataset)
                  ) {
                    return false;
                  }

                  return true;
                })
                .map(item => ({
                  ...item,
                  label: (
                    <Flex alignItems="center" className={s['label']}>
                      {t(
                        `studio.table.header.menuItem.${group.label}.${item.label}.label`
                      )}
                    </Flex>
                  ),
                  suffix: item.withCoreSymbol ? (
                    <CoreIcon
                      size={IconSize.SMALL}
                      disableDefaultColors
                      className={s['core-icon']}
                    />
                  ) : undefined,
                  // "move to folder" action has a submenu which we inject here
                  submenu:
                    item.value === BatchAction.OTHER_MOVE_TO_FOLDER ? (
                      <MoveToFolderMenu
                        onMoveToFolder={targetFolderId =>
                          confirmMoveSelectionToFolder(targetFolderId)
                        }
                      />
                    ) : item.value === BatchAction.OTHER_SELL &&
                      batchSellSubmenuItems ? (
                      <BatchSellSubmenu />
                    ) : undefined,
                  biData:
                    item.value === BatchAction.OTHER_SELL
                      ? BiData<SellYourArtClickBiEvent>({
                          evid: 353,
                          deviationid: selectedIds[0],
                          sectionname: BiContextProvidedValue,
                          items_count: selectedDeviations.length,
                          deviationid_list: selectedIds.join(','),
                        })
                      : item.biUpdateType
                        ? BiData<StudioBatchUpdateBiEvent>({
                            evid: 12,
                            update_type:
                              typeof item.biUpdateType === 'function'
                                ? item.biUpdateType(props)
                                : item.biUpdateType,
                            items_count: selectedCount ?? 0,
                          })
                        : undefined,
                })),
            }));
          return groups;
        }, {}),
    [
      t,
      props,
      confirmMoveSelectionToFolder,
      filterBySection,
      flags,
      selectedCount,
      isSmallDesktop,
      isUnpublishedDataset,
      batchSellSubmenuItems,
      selectedDeviations,
      selectedIds,
    ]
  );

  useEffect(() => {
    if (!tiers && !isLoading) {
      batchLoadTiers();
    }
  }, [tiers, isLoading, batchLoadTiers]);

  return isLoading ? (
    <LoadingIndicator view="llama" />
  ) : (
    <div className={className}>
      {Object.keys(menuItems).map(groupKey => (
        <TextButtonWithContextMenu
          key={`menu-${groupKey}`}
          standardMenuProps={{ onSelect }}
          size="small"
          variant="main"
          iconClassName={s['core-icon']}
          iconProps={{
            size: IconSize.SMALL,
            disableDefaultColors: true,
          }}
          postfix={ArrowDown}
          items={menuItems[groupKey]}
        >
          {t(`studio.table.header.menu.${groupKey}.label`)}
        </TextButtonWithContextMenu>
      ))}
    </div>
  );
};
MenuSection.displayName = 'MenuSection';

export default MenuSection;
